import React from "react";
import { LockClosedIcon, ShieldCheckIcon, CheckBadgeIcon } from "@heroicons/react/24/outline";
import { unlockUser, getAssetUrl } from "../../../services/api";
import { notify } from "../../../utils/notify"; // Using your custom notify utility
import { useTranslation } from "../../../context/I18nContext";

const LockedUsersWidget = ({ users, token, onRefresh }) => {
  const { t } = useTranslation("dashboard");

  const handleUnlock = async (userId) => {
    try {
      await unlockUser(token, userId);
      notify.success(t("notify_unlock_success", "User access restored successfully! ✨"));
      onRefresh(); // Refresh dashboard data to remove the user from the list
    } catch (error) {
      notify.error(error.message || t("notify_unlock_failed", "Failed to unlock user. Please try again."));
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm h-full flex flex-col">
      {/* Widget Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <div className="p-2 bg-red-50 rounded-lg">
            <LockClosedIcon className="w-5 h-5 text-red-500" />
          </div>
          {t("widget_locked_users", "Temporary Blocks")}
        </h3>
        <span className="text-[10px] font-extrabold bg-gray-100 text-gray-400 px-2 py-1 rounded-full uppercase tracking-widest">
          {users?.length || 0} {t("locked_users_count_suffix", "Accounts")}
        </span>
      </div>

      {/* Users List */}
      <div className="space-y-3 flex-1 overflow-y-auto custom-scrollbar pe-1">
        {users?.length > 0 ? (
          users.map((user) => (
            <div 
              key={user.id} 
              className="group flex items-center justify-between p-3 hover:bg-gray-50 rounded-2xl transition-all border border-transparent hover:border-gray-100"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center font-bold text-gray-400 border border-gray-200">
                    {/* {user.username?.[0].toUpperCase()} */}
                    {user.avatar_url ? (
            <img
              src={getAssetUrl(user.avatar_url)}
              alt="Avatar"
              className="rounded-full"
            />
          ) : (
            <img
              src={getAssetUrl(user.gender === "female" ? "/img/avatars/Female-2.png" : "/img/avatars/Male.jpg")}
              alt="Avatar"
              className="rounded-full"
            />

          )}
                  </div>
                  {/* Security Alert Pulse */}
                  <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white"></span>
                  </span>
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{user.username}</p>
                  <p className="text-[10px] text-red-500 font-semibold italic">
                    {user.failed_attempts} {t("failed_login_attempts_suffix", "Failed login attempts")}
                  </p>
                </div>
              </div>

              {/* Action: Unblock */}
              <button
                onClick={() => handleUnlock(user.id)}
                className="p-2 bg-gray-50 text-gray-400 rounded-xl hover:bg-green-600 hover:text-white transition-all shadow-sm border border-gray-100 flex items-center gap-1 group-hover:text-green-600 group-hover:bg-green-50"
                title={t("grant_access_title", "Grant Access")}
              >
                <ShieldCheckIcon className="w-4 h-4" />
                <span className="text-[10px] font-bold pe-1 hidden group-hover:inline">{t("unblock_button", "UNBLOCK")}</span>
              </button>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <CheckBadgeIcon className="w-12 h-12 text-green-100 mx-auto mb-2" />
            <p className="text-gray-400 text-xs font-medium">{t("system_secure_text", "System is secure.")}</p>
            <p className="text-gray-300 text-[10px]">{t("no_temporary_blocks_text", "No temporary blocks found.")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LockedUsersWidget;